import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const DeviceDetail = ({ deviceId, onBack, primary }) => {
  const { user, tenant } = useAuth();
  const [device, setDevice] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pingLoading, setPingLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [showInactive, setShowInactive] = useState(false);
  const [showKick, setShowKick] = useState(false);
  const [newWifiPass, setNewWifiPass] = useState('');
  const [showWifiPass, setShowWifiPass] = useState({});
  const [seedPass, setSeedPass] = useState('');
  const [showSeedInput, setShowSeedInput] = useState(false);
  const [blockedMacs, setBlockedMacs] = useState([]);
  const [speedTest, setSpeedTest] = useState(null);
  const [lcoVerified, setLcoVerified] = useState(null);
  const [wanList, setWanList] = useState([]);
  const [showAddWan, setShowAddWan] = useState(false);
  const [addingWan, setAddingWan] = useState(false);
  const [newWan, setNewWan] = useState({ vlan: '', type: 'IPoE', username: '', password: '', nat: true });
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [voipConfig, setVoipConfig] = useState({
    proxyServer: '', proxyServerPort: '5060',
    registrarServer: '', userAgentDomain: '',
    authUserName: '', authPassword: '',
    directoryNumber: '', lineEnable: false, voipEnable: false
  });
  const [voipLoading, setVoipLoading] = useState(false);
  const [voipSaving, setVoipSaving] = useState(false);
  const [showWanEdit, setShowWanEdit] = useState(false);
  const [showLanEdit, setShowLanEdit] = useState(false);
  const [showFirmware, setShowFirmware] = useState(false);
  const [wanForm, setWanForm] = useState({});
  const [lanForm, setLanForm] = useState({});
  const [firmwareUrl, setFirmwareUrl] = useState('');
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [wifiEdit, setWifiEdit] = useState({ band: null, ssid: '', pass: '' });
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [showMapHierarchy, setShowMapHierarchy] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [lcoUsers, setLcoUsers] = useState([]);
  const [hierarchyForm, setHierarchyForm] = useState({ sub_dist_id: '', lco_id: '' });
  const [customerForm, setCustomerForm] = useState({});

  const isAdmin = user && user.role !== 'customer';

  useEffect(() => { loadDetails(); }, [deviceId]);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const loadDetails = async () => {
    try {
      setLoading(true);
      const [devRes, detRes, blockedRes] = await Promise.all([
        axios.get('/api/devices'),
        axios.get('/api/devices/' + deviceId + '/details'),
        axios.get('/api/devices/' + deviceId + '/blocked-macs')
      ]);
      const dev = devRes.data.devices.find(d => d._id === deviceId);
      setDevice(dev);
      setDetails(detRes.data.details);
      setBlockedMacs(blockedRes.data.blocked.map(b => b.mac_address));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleReboot = async () => {
    if (!window.confirm('Reboot this ONT?')) return;
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/reboot');
      showMsg('Reboot command sent!');
    } catch (e) { showMsg('Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleWifiChange = async () => {
    if (!wifiEdit.pass || wifiEdit.pass.length < 8) { showMsg('Password must be at least 8 characters', 'error'); return; }
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/wifi', { band: wifiEdit.band, password: wifiEdit.pass, ssid: wifiEdit.ssid });
      showMsg('WiFi updated!');
      setWifiEdit({ band: null, ssid: '', pass: '' });
      loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleWifiToggle = async (band, enable) => {
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/wifi', { band, enabled: enable });
      showMsg('WiFi ' + (enable ? 'enabled' : 'disabled') + '!');
      loadDetails();
    } catch (e) { showMsg('Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const loadHierarchyUsers = async () => {
    try {
      const res = await axios.get('/api/users');
      const users = res.data.users || [];
      setSubUsers(users.filter(u => u.role === 'sub_dist_admin'));
      setLcoUsers(users.filter(u => u.role === 'lco_admin'));
    } catch(e) { console.error(e); }
  };

  const handleMapHierarchy = async () => {
    try {
      setActionLoading(true);
      await axios.put('/api/devices/' + deviceId, hierarchyForm);
      showMsg('Device mapped to Sub-Dist and LCO successfully!');
      setShowMapHierarchy(false);
      loadDetails();
    } catch(e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleEditCustomer = async () => {
    try {
      setActionLoading(true);
      await axios.put('/api/devices/' + deviceId, customerForm);
      showMsg('Customer details updated!');
      setShowEditCustomer(false);
      loadDetails();
    } catch(e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleBandSteering = async (enable) => {
    try {
      setActionLoading(true);
      if (enable) {
        await axios.post('/api/devices/' + deviceId + '/wifi', {
          band: 'both', ssid: details.wifi.ssid_2g || 'HomeWiFi', password: details.wifi.password_2g || ''
        });
        showMsg('Band Steering enabled! Both bands now use same SSID.');
      } else {
        await axios.post('/api/devices/' + deviceId + '/wifi', {
          band: '5g', ssid: (details.wifi.ssid_2g || 'HomeWiFi') + '_5G', password: details.wifi.password_2g || ''
        });
        showMsg('Band Steering disabled! 5G now has separate SSID.');
      }
      loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleWanUpdate = async () => {
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/wan-config', wanForm);
      showMsg('WAN configuration updated!');
      setShowWanEdit(false);
      loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleLanUpdate = async () => {
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/wan-config', lanForm);
      showMsg('LAN configuration updated!');
      setShowLanEdit(false);
      loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleFirmwareUpgrade = async () => {
    if (!firmwareUrl && !firmwareFile) { showMsg('Please enter firmware URL or select a file', 'error'); return; }
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/firmware', { url: firmwareUrl || 'file-upload' });
      showMsg('Firmware upgrade initiated! Device will reboot.');
      setShowFirmware(false);
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const loadVoip = async () => {
    setVoipLoading(true);
    try {
      const res = await axios.get('/api/devices/' + device._id + '/voip');
      if (res.data.success && res.data.voip) {
        setVoipConfig(res.data.voip);
      }
    } catch(e) { console.log('VoIP load error:', e.message); }
    finally { setVoipLoading(false); }
  };

  const saveVoip = async () => {
    setVoipSaving(true);
    try {
      await axios.post('/api/devices/' + device._id + '/voip', voipConfig);
      showMsg('VoIP configuration saved!');
    } catch(e) { showMsg(e.response?.data?.message || 'Failed to save VoIP', 'error'); }
    finally { setVoipSaving(false); }
  };

  const loadWanList = async () => {
    try {
      const res = await axios.get('/api/devices/' + deviceId + '/wan-list');
      setWanList(res.data.wan_list || []);
    } catch(e) { console.log('WAN list error:', e.message); }
  };

  const handleAddWan = async () => {
    if (!newWan.vlan) { showMsg('Enter VLAN ID', 'error'); return; }
    setAddingWan(true);
    try {
      await axios.post('/api/devices/' + deviceId + '/wan-add', newWan);
      showMsg('WAN connection added! Applying to ONT...');
      setShowAddWan(false);
      setNewWan({ vlan: '', type: 'IPoE', username: '', password: '', nat: true });
      setTimeout(() => loadWanList(), 5000);
    } catch(e) {
      showMsg(e.response?.data?.message || 'Failed to add WAN', 'error');
    } finally { setAddingWan(false); }
  };

  const handleDeleteWan = async (wanIndex) => {
    if (!window.confirm('Delete WAN connection ' + wanIndex + '?')) return;
    try {
      await axios.delete('/api/devices/' + deviceId + '/wan-delete/' + wanIndex);
      showMsg('WAN connection deleted!');
      setTimeout(() => loadWanList(), 3000);
    } catch(e) {
      showMsg(e.response?.data?.message || 'Failed to delete WAN', 'error');
    }
  };

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const res = await axios.get('/api/devices/' + deviceId + '/diagnose');
      setDiagnosis(res.data.diagnosis);
    } catch(e) {
      showMsg('Diagnosis failed: ' + e.message, 'error');
    } finally {
      setDiagnosing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tools') loadWanList();
    if (activeTab === 'voip') loadVoip();
  }, [activeTab]);

  const handleSpeedTest = async () => {
    try {
      setPingLoading(true);
      setSpeedTest({ status: 'running' });
      await axios.post('/api/devices/' + deviceId + '/speedtest');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await axios.get('/api/devices/' + deviceId + '/speedresult');
          if (res.data.result?.completed) {
            clearInterval(poll);
            setSpeedTest({ status: 'done', result: res.data.result });
            setPingLoading(false);
          } else if (attempts >= 6) {
            clearInterval(poll);
            setSpeedTest({ status: 'done', result: { completed: false, state: res.data.result?.state } });
            setPingLoading(false);
          }
        } catch(e) {
          clearInterval(poll);
          setSpeedTest({ status: 'error', message: 'Failed' });
          setPingLoading(false);
        }
      }, 60000);
    } catch(e) {
      setSpeedTest({ status: 'error', message: e.response?.data?.message || 'Failed' });
      setPingLoading(false);
    }
  };

  const handleBlockMac = async (mac, hostname, ip) => {
    const reason = window.prompt('Reason for blocking ' + (hostname || mac) + '?', 'Unauthorized device');
    if (reason === null) return;
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/block-mac', { mac, hostname, ip, reason });
      showMsg('Device blocked!');
      setBlockedMacs(prev => [...prev, mac]);
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleUnblockMac = async (mac) => {
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/unblock-mac', { mac });
      showMsg('Device unblocked!');
      setBlockedMacs(prev => prev.filter(m => m !== mac));
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const handleKickDevices = async () => {
    if (!newWifiPass || newWifiPass.length < 8) { showMsg('Password must be at least 8 characters', 'error'); return; }
    if (!window.confirm('This will disconnect ALL devices and change WiFi password to: ' + newWifiPass)) return;
    try {
      setActionLoading(true);
      await axios.post('/api/devices/' + deviceId + '/kick-devices', { newPassword: newWifiPass });
      showMsg('All devices kicked! New password set.');
      setShowKick(false);
      setNewWifiPass('');
      loadDetails();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
    finally { setActionLoading(false); }
  };

  const getSignalColor = (rx) => {
    const v = parseFloat(rx);
    if (isNaN(v)) return '#94a3b8';
    if (v >= -20) return '#10b981';
    if (v >= -25) return '#f59e0b';
    return '#ef4444';
  };

  const ipModeLabel = (mode) => {
    const modes = { '1': 'IPv4 Only', '2': 'IPv6 Only', '3': 'Dual Stack (IPv4+IPv6)' };
    return modes[String(mode)] || 'N/A';
  };

  const card = { background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '16px' };
  const inpStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
  const labelStyle = { fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '5px' };

  const Row = ({ label, value, mono, highlight }) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: '600', color: highlight || '#1e293b', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  ) : null;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${primary}22`, borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#94a3b8' }}>Loading device details...</p>
    </div>
  );

  if (!device || !details) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Device not found</div>;

  const isDualBand = details.wifi?.is_dual_band || false;
  const supportsBandSteering = isDualBand && details.wifi?.band_steering_supported || false;
  const bandSteeringEnabled = details.wifi?.band_steering_enabled || false;

  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'wan', label: '🌐 WAN / LAN' },
    { id: 'wifi', label: '📶 WiFi' },
    { id: 'devices', label: `💻 Devices (${details.lan_hosts?.filter(h => h.active).length || 0})` },
    { id: 'voip', label: '📞 VoIP' },
    ...(isAdmin ? [{ id: 'tools', label: '🔧 Tools' }] : [])
  ];

  return (
    <div>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } } @keyframes slideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }'}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
          ← Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>📡</div>
            <div>
              <h1 style={{ margin: '0 0 2px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{device.customer_name || 'Unknown Customer'}</h1>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>{device.serial_number}</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>{device.vendor} {device.model}</span>
                <span style={{ background: device.status === 'online' ? '#ecfdf5' : '#fef2f2', color: device.status === 'online' ? '#16a34a' : '#dc2626', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                  ● {device.status === 'online' ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={loadDetails} disabled={actionLoading} style={{ padding: '8px 14px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569' }}>🔃 Refresh</button>
          <button onClick={handleReboot} disabled={actionLoading} style={{ padding: '8px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#ea580c' }}>🔄 Reboot</button>
        </div>
      </div>

      {/* Message */}
      {msg.text && (
        <div style={{ background: msg.type === 'error' ? '#fef2f2' : '#ecfdf5', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: msg.type === 'error' ? '#dc2626' : '#16a34a', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', fontWeight: '500', animation: 'slideIn 0.2s ease' }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'white', padding: '4px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '9px 16px', borderRadius: '9px', border: 'none', cursor: 'pointer', background: activeTab === t.id ? primary : 'transparent', color: activeTab === t.id ? 'white' : '#64748b', fontWeight: activeTab === t.id ? '700' : '500', fontSize: '13px', whiteSpace: 'nowrap', transition: 'all 0.15s', boxShadow: activeTab === t.id ? `0 4px 12px ${primary}40` : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>👤 Customer Details</h3>
              <button onClick={() => { setShowEditCustomer(!showEditCustomer); setCustomerForm({ customer_name: device.customer_name||'', customer_phone: device.customer_phone||'', customer_id: device.customer_id||'', area: device.area||'' }); }}
                style={{ padding: '6px 12px', background: showEditCustomer?'#fef2f2':'#eff6ff', color: showEditCustomer?'#dc2626':'#2563eb', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                {showEditCustomer ? '✕ Cancel' : '✏️ Edit'}
              </button>
            </div>
            <Row label="Name" value={device.customer_name} />
            <Row label="Phone" value={device.customer_phone} />
            <Row label="Customer ID" value={device.customer_id} />
            <Row label="Area" value={device.area} />
            {showEditCustomer && (
              <div style={{ marginTop: '14px', padding: '14px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe', animation: 'slideIn 0.2s ease' }}>
                <div style={{ fontWeight: '700', fontSize: '12px', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Update Customer Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'customer_name', label: 'Full Name', placeholder: 'Customer name' },
                    { key: 'customer_phone', label: 'Phone Number', placeholder: '10-digit mobile' },
                    { key: 'customer_id', label: 'Connection ID', placeholder: 'Billing ID' },
                    { key: 'area', label: 'Area/Locality', placeholder: 'e.g. Payyanur' }
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                      <input style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '13px', boxSizing: 'border-box' }}
                        value={customerForm[f.key]||''} onChange={e=>setCustomerForm({...customerForm,[f.key]:e.target.value})} placeholder={f.placeholder} />
                    </div>
                  ))}
                </div>
                {/* LCO ID Field */}
                <div style={{ marginTop: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>LCO ID <span style={{ color: '#94a3b8', textTransform: 'none', fontWeight: '400' }}>(verify with support platform)</span></label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '13px' }}
                      value={customerForm.lco_code || ''}
                      onChange={e => { setCustomerForm({...customerForm, lco_code: e.target.value}); setLcoVerified(null); }}
                      placeholder="Enter LCO ID e.g. IFB001" />
                    <button type="button" onClick={async () => {
                      if (!customerForm.lco_code) return;
                      try {
                        const res = await axios.get('/api/users/verify-lco/' + customerForm.lco_code);
                        setLcoVerified(res.data.user);
                      } catch(e) {
                        setLcoVerified(false);
                      }
                    }} style={{ padding: '9px 14px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                      🔍 Verify
                    </button>
                  </div>
                  {lcoVerified === false && (
                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>❌ LCO not found. Add LCO to desk.pdic.support first.</div>
                  )}
                  {lcoVerified && (
                    <div style={{ fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>✅ Verified: {lcoVerified.name} | {lcoVerified.phone}</div>
                  )}
                </div>

                <button onClick={handleEditCustomer} disabled={actionLoading}
                  style={{ marginTop: '12px', width: '100%', padding: '10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                  {actionLoading ? '⏳ Saving...' : '💾 Save Customer Details'}
                </button>
              </div>
            )}
          </div>
          <div style={card}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📡 Device Info</h3>
            <Row label="Vendor" value={device.vendor} />
            <Row label="Model" value={device.model} />
            <Row label="Serial (TR-069)" value={device.serial_number} mono />
            {device.gpon_serial && <Row label="GPON Serial (Label)" value={device.gpon_serial} mono />}
            <Row label="Last Seen" value={details.wan.last_inform ? new Date(details.wan.last_inform).toLocaleString('en-IN') : null} />
            <Row label="Uptime" value={details.wan.uptime ? (()=>{ const s=parseInt(details.wan.uptime); const d=Math.floor(s/86400); const h=Math.floor((s%86400)/3600); const m=Math.floor((s%3600)/60); return (d>0?d+'d ':'')+h+'h '+m+'m'; })() : details.wan.last_boot ? (()=>{ const d=Math.floor((Date.now()-new Date(details.wan.last_boot))/1000); return Math.floor(d/3600)+'h '+Math.floor((d%3600)/60)+'m'; })() : null} />
          </div>
          <div style={card}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🔆 Optical Signal</h3>
            {details.optical.not_supported ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📡</div>Optical data not available
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  {[{ label: 'RX Power', value: details.optical.rx_power, sub: 'Received' }, { label: 'TX Power', value: details.optical.tx_power, sub: 'Transmit' }].map(s => (
                    <div key={s.label} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: getSignalColor(s.value) }}>{s.value || 'N/A'}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{s.sub}</div>
                    </div>
                  ))}
                </div>
                {details.optical.temperature && <Row label="Temperature" value={details.optical.temperature} />}
                {details.optical.voltage && <Row label="Voltage" value={details.optical.voltage} />}
                <div style={{ marginTop: '8px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b' }}>
                  Signal: <span style={{ fontWeight: '700', color: getSignalColor(details.optical.rx_power) }}>{parseFloat(details.optical.rx_power) >= -20 ? '🟢 Excellent' : parseFloat(details.optical.rx_power) >= -25 ? '🟡 Good' : '🔴 Poor'}</span>
                </div>
              </>
            )}
          </div>
          {isAdmin && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showMapHierarchy ? '14px' : 0 }}>
                <div>
                  <h3 style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🏢 Assignment</h3>
                </div>
                <button onClick={() => { if(!showMapHierarchy) loadHierarchyUsers(); setShowMapHierarchy(!showMapHierarchy); }}
                  style={{ padding: '6px 12px', background: showMapHierarchy ? '#fef2f2' : `${primary}15`, color: showMapHierarchy ? '#dc2626' : primary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                  {showMapHierarchy ? '✕ Cancel' : '🔗 Map Hierarchy'}
                </button>
              </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  {[
                    { label: 'Sub-Distribution', value: device.sub_dist_id?.name || 'Not assigned', icon: '🏘️' },
                    { label: 'LCO', value: device.lco_id?.name || 'Not assigned', icon: '👤' }
                  ].map(r => (
                    <div key={r.label} style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{r.icon} {r.label}</div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{r.value}</div>
                    </div>
                  ))}
                </div>
              {showMapHierarchy && (
                <div style={{ animation: 'slideIn 0.2s ease' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Sub-Distribution</label>
                    <select style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none' }}
                      value={hierarchyForm.sub_dist_id} onChange={e => setHierarchyForm({ ...hierarchyForm, sub_dist_id: e.target.value, lco_id: '' })}>
                      <option value=''>-- Select Sub-Distribution --</option>
                      {subUsers.map(u => <option key={u._id} value={u._id}>{u.name} {u.area ? '(' + u.area + ')' : ''}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>LCO</label>
                    <select style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none' }}
                      value={hierarchyForm.lco_id} onChange={e => setHierarchyForm({ ...hierarchyForm, lco_id: e.target.value })}>
                      <option value=''>-- Select LCO --</option>
                      {lcoUsers.filter(u => hierarchyForm.sub_dist_id && u.sub_dist_id && u.sub_dist_id.toString() === hierarchyForm.sub_dist_id).map(u => <option key={u._id} value={u._id}>{u.lco_id ? u.lco_id + ' - ' : ''}{u.name} {u.area ? '(' + u.area + ')' : ''}</option>)}
                    </select>
                  </div>
                  <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '10px', fontSize: '12px', color: '#92400e', marginBottom: '12px' }}>
                    💡 Mapping ensures complaints from this customer are automatically assigned to the correct LCO technician.
                  </div>
                  <button onClick={handleMapHierarchy} disabled={actionLoading}
                    style={{ width: '100%', padding: '11px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                    {actionLoading ? '⏳ Saving...' : '💾 Save Assignment'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={card}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📈 Traffic Stats</h3>
            {details.wan.bytes_received ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ background: '#ecfdf5', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#16a34a' }}>{(details.wan.bytes_received/1024/1024/1024).toFixed(2)} GB</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>⬇️ Downloaded</div>
                </div>
                <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#2563eb' }}>{(details.wan.bytes_sent/1024/1024).toFixed(0)} MB</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>⬆️ Uploaded</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>No traffic data available</div>
            )}
          </div>
        </div>
      )}

      {/* WAN/LAN TAB */}
      {activeTab === 'wan' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🌐 WAN Configuration</h3>
              {isAdmin && (
                <button onClick={() => { setShowWanEdit(!showWanEdit); setShowLanEdit(false); setWanForm({ connection_type: details.wan.connection_type||'PPPoE', pppoe_user: details.wan.pppoe_user||'', pppoe_pass: '', vlan: details.wan.vlan||'', ip_mode: String(details.wan.ip_mode||'1'), nat_enabled: details.wan.nat_enabled, dns_servers: details.wan.dns_servers||'' }); }}
                  style={{ padding: '6px 12px', background: showWanEdit ? '#fef2f2' : '#eff6ff', color: showWanEdit ? '#dc2626' : '#2563eb', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                  {showWanEdit ? '✕ Cancel' : '✏️ Edit WAN'}
                </button>
              )}
            </div>
            <Row label="Connection Type" value={details.wan.connection_type} />
            <Row label="IP Address" value={details.wan.ip} mono />
            <Row label="MAC Address" value={details.wan.mac} mono />
            <Row label="Status" value={details.wan.status} highlight={details.wan.status === 'Connected' ? '#16a34a' : '#ef4444'} />
            <Row label="PPPoE Username" value={details.wan.pppoe_user} mono />
            <Row label="VLAN ID" value={details.wan.vlan || 'Not set'} />
            <Row label="IP Mode" value={ipModeLabel(details.wan.ip_mode)} />
            <Row label="NAT" value={details.wan.nat_enabled ? 'Enabled' : 'Disabled'} />
            <Row label="DNS Servers" value={details.wan.dns_servers} mono />
            {showWanEdit && (
              <div style={{ marginTop: '16px', padding: '16px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', animation: 'slideIn 0.2s ease' }}>
                <div style={{ fontWeight: '700', fontSize: '12px', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Update WAN Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={labelStyle}>Connection Type</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['PPPoE','IPoE','Static'].map(ct => (
                        <button key={ct} onClick={() => setWanForm({...wanForm, connection_type: ct})}
                          style={{ flex: 1, padding: '10px', border: `2px solid ${wanForm.connection_type===ct ? '#2563eb' : '#e2e8f0'}`, borderRadius: '8px', background: wanForm.connection_type===ct ? '#eff6ff' : 'white', color: wanForm.connection_type===ct ? '#2563eb' : '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s' }}>
                          {ct === 'PPPoE' ? '🔐 PPPoE' : ct === 'IPoE' ? '🌐 IPoE (DHCP)' : '📌 Static IP'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {wanForm.connection_type === 'PPPoE' && (
                    <>
                      <div><label style={labelStyle}>PPPoE Username</label><input style={inpStyle} value={wanForm.pppoe_user||''} onChange={e=>setWanForm({...wanForm,pppoe_user:e.target.value})} placeholder="PPPoE username" /></div>
                      <div><label style={labelStyle}>PPPoE Password</label><input style={inpStyle} type="password" value={wanForm.pppoe_pass||''} onChange={e=>setWanForm({...wanForm,pppoe_pass:e.target.value})} placeholder="Leave blank to keep current" /></div>
                    </>
                  )}

                  {wanForm.connection_type === 'Static' && (
                    <>
                      <div><label style={labelStyle}>Static IP Address</label><input style={inpStyle} value={wanForm.static_ip||''} onChange={e=>setWanForm({...wanForm,static_ip:e.target.value})} placeholder="e.g. 203.0.113.10" /></div>
                      <div><label style={labelStyle}>Subnet Mask</label><input style={inpStyle} value={wanForm.static_mask||''} onChange={e=>setWanForm({...wanForm,static_mask:e.target.value})} placeholder="e.g. 255.255.255.0" /></div>
                      <div><label style={labelStyle}>Default Gateway</label><input style={inpStyle} value={wanForm.static_gw||''} onChange={e=>setWanForm({...wanForm,static_gw:e.target.value})} placeholder="e.g. 203.0.113.1" /></div>
                    </>
                  )}

                  <div>
                    <label style={labelStyle}>Service Type</label>
                    <select style={inpStyle} value={wanForm.service_type||'INTERNET'} onChange={e=>setWanForm({...wanForm,service_type:e.target.value})}>
                      <option value="INTERNET">INTERNET</option>
                      <option value="TR069">TR069</option>
                      <option value="TR069_INTERNET">TR069_INTERNET</option>
                      <option value="VOIP">VOIP</option>
                      <option value="INTERNET_VOIP">INTERNET + VOIP</option>
                      <option value="TR069_INTERNET_VOIP">TR069 + INTERNET + VOIP</option>
                      <option value="IPTV">IPTV</option>
                      <option value="INTERNET_IPTV">INTERNET + IPTV</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>VLAN ID <span style={{ color: '#94a3b8', fontWeight: '400', textTransform: 'none' }}>(current: {details.wan.vlan || 'Not set'})</span></label>
                    <input style={inpStyle} value={wanForm.vlan||''} onChange={e=>setWanForm({...wanForm,vlan:e.target.value})} placeholder={details.wan.vlan ? 'Current: '+details.wan.vlan : 'e.g. 100'} />
                  </div>
                  <div>
                    <label style={labelStyle}>IP Mode</label>
                    <select style={inpStyle} value={wanForm.ip_mode||'1'} onChange={e=>setWanForm({...wanForm,ip_mode:e.target.value})}>
                      <option value="1">IPv4 Only</option>
                      <option value="2">IPv6 Only</option>
                      <option value="3">Dual Stack (IPv4+IPv6)</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={labelStyle}>DNS Servers <span style={{ color: '#94a3b8', fontWeight: '400', textTransform: 'none' }}>(comma separated)</span></label>
                    <input style={inpStyle} value={wanForm.dns_servers||''} onChange={e=>setWanForm({...wanForm,dns_servers:e.target.value})} placeholder="e.g. 8.8.8.8,8.8.4.4" />
                  </div>
                  <div>
                    <label style={labelStyle}>NAT</label>
                    <select style={inpStyle} value={wanForm.nat_enabled?'true':'false'} onChange={e=>setWanForm({...wanForm,nat_enabled:e.target.value==='true'})}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>
                </div>
                <button onClick={handleWanUpdate} disabled={actionLoading} style={{ marginTop: '14px', width: '100%', padding: '11px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                  {actionLoading ? '⏳ Updating...' : '💾 Apply WAN Configuration'}
                </button>
              </div>
            )}
          </div>

          {/* WAN Connections List + Add WAN */}
          {isAdmin && (
            <div style={{ ...card, gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🌐 All WAN Connections</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={loadWanList} style={{ padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>🔄 Refresh</button>
                  <button onClick={() => setShowAddWan(!showAddWan)}
                    style={{ padding: '6px 12px', background: showAddWan ? '#fef2f2' : '#0ea5e9', color: showAddWan ? '#dc2626' : 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                    {showAddWan ? '✕ Cancel' : '+ Add WAN'}
                  </button>
                </div>
              </div>

              {/* WAN List */}
              {wanList.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 12px' }}>Click Refresh to load all WAN connections.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                  {wanList.map(w => (
                    <div key={w.index} style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0', borderLeft: '4px solid ' + (w.status === 'Connected' ? '#22c55e' : w.index === '1' ? '#0ea5e9' : '#94a3b8') }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>
                          WAN {w.index} {
                          w.is_management ? '🔧 Management' :
                          w.ip && w.status === 'Connected' ? '🌐 Internet' :
                          w.vlan ? '🌐 Data' :
                          '🔀 Bridge'
                        }
                        </span>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '10px', background: w.status === 'Connected' ? '#dcfce7' : '#f1f5f9', color: w.status === 'Connected' ? '#16a34a' : '#64748b', fontWeight: '600' }}>
                            {w.status || 'Unknown'}
                          </span>
                          {w.index !== '1' && (
                            <button onClick={() => handleDeleteWan(w.index)}
                              style={{ padding: '2px 7px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.6' }}>
                        {w.vlan && <div>VLAN: <strong>{w.vlan}</strong></div>}
                        {w.type && <div>Type: <strong>{w.type}</strong></div>}
                        {w.ip && <div>IP: <strong style={{ fontFamily: 'monospace' }}>{w.ip}</strong></div>}
                        {w.username && <div>User: <strong>{w.username}</strong></div>}
                        {w.wan_name && <div>Name: <strong>{w.wan_name}</strong></div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add WAN Form */}
              {showAddWan && (
                <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '16px', border: '1px solid #bae6fd', marginTop: '4px' }}>
                  <div style={{ fontWeight: '700', fontSize: '12px', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Add New WAN Connection</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={labelStyle}>VLAN ID *</label>
                      <input type="number" value={newWan.vlan} onChange={e => setNewWan({...newWan, vlan: e.target.value})}
                        placeholder="e.g. 34" style={inpStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Service Type</label>
                      <select value={newWan.service_type||'INTERNET'} onChange={e => setNewWan({...newWan, service_type: e.target.value})} style={inpStyle}>
                        <option value="INTERNET">INTERNET</option>
                        <option value="TR069">TR069</option>
                        <option value="TR069_INTERNET">TR069_INTERNET</option>
                        <option value="VOIP">VOIP</option>
                        <option value="IPTV">IPTV</option>
                        <option value="INTERNET_VOIP">INTERNET + VOIP</option>
                        <option value="TR069_INTERNET_VOIP">TR069 + INTERNET + VOIP</option>
                        <option value="INTERNET_IPTV">INTERNET + IPTV</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Connection Type</label>
                      <select value={newWan.type} onChange={e => setNewWan({...newWan, type: e.target.value})} style={inpStyle}>
                        <option value="IPoE">🌐 IPoE (DHCP)</option>
                        <option value="PPPoE">🔐 PPPoE</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>NAT</label>
                      <select value={newWan.nat} onChange={e => setNewWan({...newWan, nat: e.target.value === 'true'})} style={inpStyle}>
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    </div>
                  </div>
                  {newWan.type === 'PPPoE' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={labelStyle}>PPPoE Username</label>
                        <input value={newWan.username} onChange={e => setNewWan({...newWan, username: e.target.value})}
                          placeholder="username@isp" style={inpStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>PPPoE Password</label>
                        <input value={newWan.password} onChange={e => setNewWan({...newWan, password: e.target.value})}
                          placeholder="password" style={inpStyle} />
                      </div>
                    </div>
                  )}
                  <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: '#92400e' }}>
                    ⚠️ This adds a NEW WAN connection. Existing WAN connections will NOT be touched.
                  </div>
                  <button onClick={handleAddWan} disabled={addingWan}
                    style={{ padding: '10px 24px', background: addingWan ? '#e2e8f0' : '#0ea5e9', color: addingWan ? '#94a3b8' : 'white', border: 'none', borderRadius: '8px', cursor: addingWan ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                    {addingWan ? '⏳ Adding...' : '+ Add WAN Connection'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🏠 LAN Configuration</h3>
              {isAdmin && (
                <button onClick={() => { setShowLanEdit(!showLanEdit); setShowWanEdit(false); setLanForm({ lan_ip: details.lan?.lan_ip||'', lan_mask: details.lan?.lan_mask||'', dhcp_min: details.lan?.min_address||'', dhcp_max: details.lan?.max_address||'' }); }}
                  style={{ padding: '6px 12px', background: showLanEdit ? '#fef2f2' : '#ecfdf5', color: showLanEdit ? '#dc2626' : '#16a34a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                  {showLanEdit ? '✕ Cancel' : '✏️ Edit LAN'}
                </button>
              )}
            </div>
            {details.lan ? (
              <>
                <Row label="LAN IP Address" value={details.lan.lan_ip || details.lan.ip_routers} mono />
                <Row label="Subnet Mask" value={details.lan.lan_mask || details.lan.subnet_mask} mono />
                <Row label="DHCP Server" value={details.lan.dhcp_enabled ? '🟢 Enabled' : '🔴 Disabled'} />
                <Row label="DHCP Start" value={details.lan.min_address} mono />
                <Row label="DHCP End" value={details.lan.max_address} mono />
                <Row label="Gateway" value={details.lan.ip_routers} mono />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                LAN data loading... Refresh after 5 minutes.
              </div>
            )}
            {showLanEdit && (
              <div style={{ marginTop: '16px', padding: '16px', background: '#ecfdf5', borderRadius: '12px', border: '1px solid #bbf7d0', animation: 'slideIn 0.2s ease' }}>
                <div style={{ fontWeight: '700', fontSize: '12px', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Update LAN Settings</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>LAN IP Address</label><input style={inpStyle} value={lanForm.lan_ip||''} onChange={e=>setLanForm({...lanForm,lan_ip:e.target.value})} placeholder="e.g. 192.168.1.1" /></div>
                  <div><label style={labelStyle}>Subnet Mask</label><input style={inpStyle} value={lanForm.lan_mask||''} onChange={e=>setLanForm({...lanForm,lan_mask:e.target.value})} placeholder="e.g. 255.255.255.0" /></div>
                  <div><label style={labelStyle}>DHCP Start IP</label><input style={inpStyle} value={lanForm.dhcp_min||''} onChange={e=>setLanForm({...lanForm,dhcp_min:e.target.value})} placeholder="e.g. 192.168.1.100" /></div>
                  <div><label style={labelStyle}>DHCP End IP</label><input style={inpStyle} value={lanForm.dhcp_max||''} onChange={e=>setLanForm({...lanForm,dhcp_max:e.target.value})} placeholder="e.g. 192.168.1.200" /></div>
                </div>
                <button onClick={handleLanUpdate} disabled={actionLoading} style={{ marginTop: '14px', width: '100%', padding: '11px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                  {actionLoading ? '⏳ Updating...' : '💾 Apply LAN Configuration'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WIFI TAB */}
      {activeTab === 'wifi' && (
        <div>
          {/* Band Steering - only show for ONTs that support it */}
          {supportsBandSteering && (
            <div style={{ ...card, marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: bandSteeringEnabled ? `${primary}15` : '#f8fafc', border: `2px solid ${bandSteeringEnabled ? primary : '#e2e8f0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', transition: 'all 0.3s' }}>
                    🔀
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>Band Steering</h3>
                      <span style={{ background: bandSteeringEnabled ? '#ecfdf5' : '#f1f5f9', color: bandSteeringEnabled ? '#16a34a' : '#64748b', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', border: `1px solid ${bandSteeringEnabled ? '#bbf7d0' : '#e2e8f0'}` }}>
                        {bandSteeringEnabled ? '● Enabled' : '○ Disabled'}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                      {bandSteeringEnabled
                        ? `Both bands share SSID "${details.wifi.ssid_2g}". Devices auto-connect to optimal band.`
                        : `2.4G: "${details.wifi.ssid_2g}" | 5G: "${details.wifi.ssid_5g}". Devices manually choose band.`}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <div onClick={() => !actionLoading && handleBandSteering(!bandSteeringEnabled)}
                    style={{ width: '56px', height: '30px', borderRadius: '15px', background: bandSteeringEnabled ? primary : '#e2e8f0', cursor: actionLoading ? 'not-allowed' : 'pointer', position: 'relative', transition: 'all 0.3s', boxShadow: bandSteeringEnabled ? `0 0 0 3px ${primary}25` : 'none' }}>
                    <div style={{ position: 'absolute', top: '3px', left: bandSteeringEnabled ? '29px' : '3px', width: '24px', height: '24px', borderRadius: '50%', background: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', transition: 'left 0.3s' }} />
                  </div>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>{bandSteeringEnabled ? 'Click to disable' : 'Click to enable'}</span>
                </div>
              </div>
              {!bandSteeringEnabled && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
                  💡 Enable Band Steering to let the ONT automatically direct devices to 2.4G or 5G for best performance.
                </div>
              )}
            </div>
          )}

          {/* WiFi Band Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              { band: '2g', label: '2.4 GHz', ssid: details.wifi.ssid_2g, pass: details.wifi.password_2g, enabled: details.wifi.enabled_2g },
              ...(isDualBand ? [{ band: '5g', label: '5 GHz', ssid: details.wifi.ssid_5g, pass: details.wifi.password_5g, enabled: details.wifi.enabled_5g }] : [])
            ].map(w => (
              <div key={w.band} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📶 {w.label} WiFi</h3>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleWifiToggle(w.band, !w.enabled)} disabled={actionLoading}
                      style={{ padding: '5px 10px', background: w.enabled ? '#fef2f2' : '#ecfdf5', color: w.enabled ? '#dc2626' : '#16a34a', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                      {w.enabled ? '🔴 Disable' : '🟢 Enable'}
                    </button>
                    <button onClick={() => setWifiEdit(wifiEdit.band === w.band ? { band: null, ssid: '', pass: '' } : { band: w.band, ssid: w.ssid || '', pass: '' })}
                      style={{ padding: '5px 10px', background: wifiEdit.band === w.band ? '#fef2f2' : '#eff6ff', color: wifiEdit.band === w.band ? '#dc2626' : '#2563eb', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                      {wifiEdit.band === w.band ? '✕' : '✏️ Edit'}
                    </button>
                  </div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>SSID</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{w.ssid || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Status</span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: w.enabled ? '#16a34a' : '#dc2626' }}>{w.enabled ? '● Active' : '● Disabled'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Password</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {w.pass ? (
                        <>
                          <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'monospace', color: '#1e293b', letterSpacing: showWifiPass[w.band] ? '0' : '2px' }}>
                            {showWifiPass[w.band] ? w.pass : '••••••••'}
                          </span>
                          <button onClick={() => setShowWifiPass({...showWifiPass, [w.band]: !showWifiPass[w.band]})}
                            style={{ padding: '2px 8px', fontSize: '11px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '5px', cursor: 'pointer', color: '#475569', fontWeight: '600' }}>
                            {showWifiPass[w.band] ? '🙈 Hide' : '👁 Show'}
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>⚠ Not set in DB</span>
                      )}
                    </div>
                  </div>
                  {!w.pass && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '6px', fontWeight: '600' }}>Seed WiFi Password to DB</div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input type="text" placeholder="Enter current WiFi password"
                          value={seedPass} onChange={e => setSeedPass(e.target.value)}
                          style={{ flex: 1, padding: '6px 10px', fontSize: '12px', border: '1px solid #fcd34d', borderRadius: '6px', background: 'white' }} />
                        <button disabled={seedPass.length < 8 || actionLoading}
                          onClick={async () => {
                            try {
                              await axios.post('/api/devices/' + deviceId + '/wifi', { band: w.band, password: seedPass, ssid: w.ssid });
                              showMsg('Password saved to DB!'); setSeedPass(''); loadDetails();
                            } catch(e) { showMsg('Failed to save', 'error'); }
                          }}
                          style={{ padding: '6px 12px', fontSize: '12px', background: seedPass.length >= 8 ? '#f59e0b' : '#e2e8f0', color: seedPass.length >= 8 ? 'white' : '#94a3b8', border: 'none', borderRadius: '6px', cursor: seedPass.length >= 8 ? 'pointer' : 'not-allowed', fontWeight: '700' }}>
                          💾 Save
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {wifiEdit.band === w.band && (
                  <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '14px', border: '1px solid #bfdbfe', animation: 'slideIn 0.2s ease' }}>
                    <label style={labelStyle}>New SSID (Network Name)</label>
                    <input style={{ ...inpStyle, marginBottom: '10px' }} value={wifiEdit.ssid} onChange={e => setWifiEdit({ ...wifiEdit, ssid: e.target.value })} placeholder={w.ssid || 'WiFi name'} />
                    <label style={labelStyle}>New Password (min 8 chars)</label>
                    <input style={{ ...inpStyle, marginBottom: '12px' }} type="text" value={wifiEdit.pass} onChange={e => setWifiEdit({ ...wifiEdit, pass: e.target.value })} placeholder="New WiFi password" />
                    <button onClick={handleWifiChange} disabled={actionLoading || wifiEdit.pass.length < 8}
                      style={{ width: '100%', padding: '10px', background: wifiEdit.pass.length >= 8 ? '#2563eb' : '#e2e8f0', color: wifiEdit.pass.length >= 8 ? 'white' : '#94a3b8', border: 'none', borderRadius: '8px', cursor: wifiEdit.pass.length >= 8 ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '13px' }}>
                      {actionLoading ? '⏳ Updating...' : '💾 Update WiFi'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DEVICES TAB */}
      {activeTab === 'devices' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>💻 Connected Devices</h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{details.lan_hosts?.filter(h=>h.active).length||0} active · {details.lan_hosts?.length||0} total</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
                Show inactive
              </label>
              <button onClick={() => setShowKick(!showKick)} style={{ padding: '7px 14px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                🚫 Kick All
              </button>
            </div>
          </div>
          {showKick && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '16px', animation: 'slideIn 0.2s ease' }}>
              <div style={{ fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>⚠️ Kick All WiFi Devices</div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>Changes WiFi password and disconnects ALL devices.</div>
              <input style={{ ...inpStyle, marginBottom: '10px' }} placeholder="New WiFi password (min 8 chars)" value={newWifiPass} onChange={e => setNewWifiPass(e.target.value)} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleKickDevices} disabled={actionLoading || newWifiPass.length < 8}
                  style={{ flex: 1, padding: '9px', background: newWifiPass.length >= 8 ? '#dc2626' : '#e2e8f0', color: newWifiPass.length >= 8 ? 'white' : '#94a3b8', border: 'none', borderRadius: '8px', cursor: newWifiPass.length >= 8 ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '13px' }}>
                  {actionLoading ? '⏳...' : '🚫 Confirm Kick All'}
                </button>
                <button onClick={() => { setShowKick(false); setNewWifiPass(''); }} style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              </div>
            </div>
          )}
          {details.lan_hosts?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>💻</div>No devices connected
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['#', 'Hostname', 'IP Address', 'MAC Address', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {details.lan_hosts?.filter(h => showInactive || h.active).map((h, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc', background: blockedMacs.includes(h.mac) ? '#fff7ed' : h.active ? 'white' : '#fafafa', opacity: h.active ? 1 : 0.6 }}>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#94a3b8' }}>{i+1}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{h.hostname || 'Unknown'}</div>
                      {blockedMacs.includes(h.mac) && <span style={{ background: '#f97316', color: 'white', padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>BLOCKED</span>}
                    </td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: '13px', color: '#475569' }}>{h.ip}</td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: '12px', color: '#64748b' }}>{h.mac}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: h.active ? '#ecfdf5' : '#f8fafc', color: h.active ? '#16a34a' : '#94a3b8', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor' }} />
                        {h.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {blockedMacs.includes(h.mac) ? (
                        <button onClick={() => handleUnblockMac(h.mac)} disabled={actionLoading} style={{ padding: '5px 12px', background: '#ecfdf5', color: '#16a34a', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>✅ Unblock</button>
                      ) : (
                        <button onClick={() => handleBlockMac(h.mac, h.hostname, h.ip)} disabled={actionLoading} style={{ padding: '5px 12px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>🚫 Block</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TOOLS TAB */}
      {activeTab === 'tools' && isAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>⚡ Speed Test (TR-069)</h3>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#94a3b8' }}>ONT downloads test file and reports actual speed</p>
            <button onClick={handleSpeedTest} disabled={pingLoading || actionLoading}
              style={{ width: '100%', padding: '12px', background: pingLoading ? '#f1f5f9' : '#8b5cf6', color: pingLoading ? '#94a3b8' : 'white', border: 'none', borderRadius: '10px', cursor: pingLoading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
              {pingLoading ? '⏳ Running speed test...' : '⚡ Run Speed Test'}
            </button>
            {speedTest && (
              <div style={{ marginTop: '12px', padding: '14px', background: speedTest.status === 'error' ? '#fef2f2' : '#f5f3ff', borderRadius: '10px' }}>
                {speedTest.status === 'running' && <div style={{ textAlign: 'center', color: '#7c3aed', fontSize: '13px' }}>⏳ Test running... Results in ~5 mins. Click Refresh after.</div>}
                {speedTest.status === 'error' && <div style={{ color: '#dc2626' }}>❌ {speedTest.message}</div>}
                {speedTest.status === 'done' && speedTest.result?.completed && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '42px', fontWeight: '800', color: primary }}>{speedTest.result.download_mbps}</div>
                    <div style={{ color: '#64748b', marginBottom: '12px' }}>Mbps Download</div>
                    <Row label="Duration" value={speedTest.result.duration + ' sec'} />
                    <Row label="Downloaded" value={speedTest.result.bytes_mb + ' MB'} />
                  </div>
                )}
                {speedTest.status === 'done' && !speedTest.result?.completed && (
                  <div style={{ textAlign: 'center', color: '#64748b', fontSize: '13px' }}>⚠️ Test state: {speedTest.result?.state || 'Pending'}</div>
                )}
              </div>
            )}
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🔍 Smart Diagnosis</h3>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#94a3b8' }}>Auto-detect network issues: fiber, PPPoE, signal or ISP problems</p>
            <button onClick={handleDiagnose} disabled={diagnosing}
              style={{ width: '100%', padding: '12px', background: diagnosing ? '#f1f5f9' : '#0ea5e9', color: diagnosing ? '#94a3b8' : 'white', border: 'none', borderRadius: '10px', cursor: diagnosing ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
              {diagnosing ? '⏳ Diagnosing...' : '🔍 Run Diagnosis'}
            </button>
            {diagnosis && (
              <div style={{ marginTop: '12px', padding: '14px', background: diagnosis.issue_type === 'NO_ISSUE' ? '#f0fdf4' : diagnosis.priority === 'high' ? '#fef2f2' : '#fff7ed', borderRadius: '10px', borderLeft: '4px solid ' + (diagnosis.issue_type === 'NO_ISSUE' ? '#22c55e' : diagnosis.priority === 'high' ? '#ef4444' : '#f97316') }}>
                <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '8px', color: diagnosis.issue_type === 'NO_ISSUE' ? '#16a34a' : diagnosis.priority === 'high' ? '#dc2626' : '#ea580c' }}>
                  {diagnosis.issue_type === 'NO_ISSUE' ? '✅ All Systems Normal' :
                   diagnosis.issue_type === 'ONT_OFFLINE' ? '🔴 ONT Offline' :
                   diagnosis.issue_type === 'PPPOE_DISCONNECTED' ? '🟡 PPPoE Disconnected' :
                   diagnosis.issue_type === 'NO_IP_ASSIGNED' ? '🟡 No IP Assigned' :
                   diagnosis.issue_type === 'WEAK_SIGNAL' ? '🟠 Weak Signal' :
             diagnosis.issue_type === 'MULTIPLE_OFFLINE' ? '🚨 Multiple ONTs Offline - PON Fault' :
             diagnosis.issue_type === 'LCO_TOTAL_OUTAGE' ? '🚨 CRITICAL - LCO Total Outage' :
             diagnosis.issue_type}
                </div>
                <div style={{ fontSize: '13px', color: '#334155', marginBottom: '8px' }}>{diagnosis.description}</div>
                {diagnosis.recommendation && (
                  <div style={{ fontSize: '12px', color: '#0369a1', background: '#e0f2fe', padding: '8px', borderRadius: '6px', marginBottom: '8px' }}>
                    💡 {diagnosis.recommendation}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  Assign to: <strong>{diagnosis.assign_to === 'lco' ? '🔧 LCO Technician' : '📞 ISP Support'}</strong>
                </div>
                {diagnosis.checks && (
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {Object.entries(diagnosis.checks).map(([k,v]) => (
                      <span key={k} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: v === true ? '#dcfce7' : v === false ? '#fee2e2' : '#f1f5f9', color: v === true ? '#16a34a' : v === false ? '#dc2626' : '#475569' }}>
                        {k}: {typeof v === 'boolean' ? (v ? '✅' : '❌') : v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>⬆️ Firmware Upgrade</h3>
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', marginBottom: '14px', fontSize: '12px', color: '#92400e' }}>
              ⚠️ Device will reboot. Ensure firmware is compatible with {device.vendor} {device.model}.
            </div>
            <label style={labelStyle}>Option 1: Upload from PC</label>
            <div style={{ border: '2px dashed #c4b5fd', borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: '#faf5ff', marginBottom: '12px' }}
              onClick={() => document.getElementById('fw-file').click()}>
              <input id="fw-file" type="file" accept=".bin,.img,.tar,.gz,.zip" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if(f){setFirmwareFile(f); setFirmwareUrl('');} }} />
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>📁</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#7c3aed' }}>{firmwareFile ? '✅ '+firmwareFile.name : 'Click to select firmware file'}</div>
              <div style={{ fontSize: '11px', color: '#a78bfa', marginTop: '4px' }}>.bin .img .tar.gz .zip</div>
            </div>
            <label style={labelStyle}>Option 2: Firmware URL</label>
            <input value={firmwareUrl} onChange={e => { setFirmwareUrl(e.target.value); setFirmwareFile(null); }}
              placeholder="http://your-server.com/firmware.bin"
              style={{ ...inpStyle, marginBottom: '14px' }} />
            <button onClick={handleFirmwareUpgrade} disabled={actionLoading || (!firmwareUrl && !firmwareFile)}
              style={{ width: '100%', padding: '12px', background: firmwareUrl || firmwareFile ? '#7c3aed' : '#e2e8f0', color: firmwareUrl || firmwareFile ? 'white' : '#94a3b8', border: 'none', borderRadius: '10px', cursor: firmwareUrl || firmwareFile ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '14px' }}>
              {actionLoading ? '⏳ Upgrading...' : '⬆️ Start Firmware Upgrade'}
            </button>
          </div>

          <div style={{ ...card, gridColumn: '1/-1' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>🔧 Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
              {[
                { label: '🔄 Reboot ONT', sub: 'Restart device remotely', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', action: handleReboot },
                { label: '🔃 Refresh Data', sub: 'Fetch latest parameters', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', action: loadDetails },
                { label: '🚫 Kick All Devices', sub: 'Disconnect all WiFi users', bg: '#fef2f2', color: '#dc2626', border: '#fecaca', action: () => { setActiveTab('devices'); setShowKick(true); } }
              ].map(a => (
                <button key={a.label} onClick={a.action} disabled={actionLoading}
                  style={{ padding: '16px', background: a.bg, color: a.color, border: `1px solid ${a.border}`, borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', textAlign: 'left', transition: 'all 0.15s' }}>
                  {a.label}
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '400', marginTop: '4px' }}>{a.sub}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* VoIP Tab */}
      {activeTab === 'voip' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {voipLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading VoIP config...</div>
          ) : (
            <>
              <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>📞 VoIP Configuration</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>VoIP Enable</span>
                    <div onClick={() => setVoipConfig({...voipConfig, voipEnable: !voipConfig.voipEnable})}
                      style={{ width: '44px', height: '24px', borderRadius: '12px', background: voipConfig.voipEnable ? '#10b981' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: '2px', left: voipConfig.voipEnable ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div><label style={labelStyle}>SIP Proxy Server</label><input style={inpStyle} value={voipConfig.proxyServer} onChange={e => setVoipConfig({...voipConfig, proxyServer: e.target.value})} placeholder="e.g. sip.provider.com" /></div>
                  <div><label style={labelStyle}>SIP Proxy Port</label><input style={inpStyle} value={voipConfig.proxyServerPort} onChange={e => setVoipConfig({...voipConfig, proxyServerPort: e.target.value})} placeholder="5060" /></div>
                  <div><label style={labelStyle}>Registrar Server</label><input style={inpStyle} value={voipConfig.registrarServer} onChange={e => setVoipConfig({...voipConfig, registrarServer: e.target.value})} placeholder="e.g. sip.provider.com" /></div>
                  <div><label style={labelStyle}>SIP Domain</label><input style={inpStyle} value={voipConfig.userAgentDomain} onChange={e => setVoipConfig({...voipConfig, userAgentDomain: e.target.value})} placeholder="e.g. provider.com" /></div>
                  <div><label style={labelStyle}>SIP Username</label><input style={inpStyle} value={voipConfig.authUserName} onChange={e => setVoipConfig({...voipConfig, authUserName: e.target.value})} placeholder="SIP auth username" /></div>
                  <div><label style={labelStyle}>SIP Password</label><input style={inpStyle} type="password" value={voipConfig.authPassword} onChange={e => setVoipConfig({...voipConfig, authPassword: e.target.value})} placeholder="SIP auth password" /></div>
                  <div><label style={labelStyle}>Phone Number</label><input style={inpStyle} value={voipConfig.directoryNumber} onChange={e => setVoipConfig({...voipConfig, directoryNumber: e.target.value})} placeholder="e.g. 9876543210" /></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '24px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Line Enable</span>
                    <div onClick={() => setVoipConfig({...voipConfig, lineEnable: !voipConfig.lineEnable})}
                      style={{ width: '44px', height: '24px', borderRadius: '12px', background: voipConfig.lineEnable ? '#10b981' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: '2px', left: voipConfig.lineEnable ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '20px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '13px', color: '#166534' }}>
                  💡 VoIP settings will be applied to the ONT immediately after saving.
                </div>
                <button onClick={saveVoip} disabled={voipSaving}
                  style={{ marginTop: '16px', padding: '12px 28px', background: voipSaving ? '#ccc' : primary, color: 'white', border: 'none', borderRadius: '10px', cursor: voipSaving ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                  {voipSaving ? '⏳ Saving...' : '💾 Save VoIP Config'}
                </button>
              </div>
              <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🎵 Supported Codecs</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {['G.711MuLaw', 'G.711ALaw', 'G.729'].map(codec => (
                    <span key={codec} style={{ background: '#e0f2fe', color: '#0369a1', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>{codec}</span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceDetail;
